import Box from '@mui/material/Box'
import Fade from '@mui/material/Fade'
import { alpha } from '@mui/material/styles'
import Typography from '@components/_mui/Typography'

import useDevices from '@shared/twilio/src/hooks/useDevices'
import { useMicPermission } from '@shared/twilio/src/hooks/useMediaPermissions'
import { getMediaErrorDescription } from '@shared/twilio/src/utils'
import { VoiceCallStatus } from '@shared/utils'

export const WIDGET_WIDTH = 170
export const WIDGET_HEIGHT = 340

export const styles = {
  container: (theme) => ({
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    width: WIDGET_WIDTH,
    height: WIDGET_HEIGHT,
    p: 1,
    pt: 2,
    borderRadius: 3,
    border: '1px solid',
    borderColor: 'divider',
    backgroundColor: alpha(theme.palette.background.paper, 0.6),
    backdropFilter: `blur(8px) saturate(180%)`,
    WebkitBackdropFilter: `blur(8px) saturate(180%)`,
    boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.15), 0 2px 8px 0 rgba(0, 0, 0, 0.08)',
  }),
  handle: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1,
    height: 20,
    width: '100%',
    cursor: 'grab',
    display: 'flex',
    justifyContent: 'center',
    color: 'text.secondary',
  },
  bar: {
    width: 100,
    height: 4,
    borderRadius: 2,
    mt: 0.5,
    bgcolor: 'divider',
  },
  close: {
    zIndex: 2,
    position: 'absolute',
    top: 4,
    right: 2,
  },
  pad: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    alignSelf: 'center',
    gap: 1,
    pt: 2,
  },
  input: {
    backgroundColor: 'transparent',
    '& input': { backgroundColor: 'transparent' },
  },
  padButton: (theme) => ({
    backgroundColor: alpha(theme.palette.background.paper, 0.4),
    borderColor: 'divider',
    backdropFilter: 'blur(4px)',
    WebkitBackdropFilter: 'blur(4px)',
    transition: theme.transitions.create(['background-color', 'transform', 'box-shadow']),
    '&:hover': {
      backgroundColor: alpha(theme.palette.background.paper, 0.7),
      borderColor: alpha(theme.palette.primary.main, 0.4),
    },
    '&:active': { transform: 'scale(0.94)' },
  }),
  actionButton: {
    boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.15)',
    transition: (theme) => theme.transitions.create(['transform', 'box-shadow']),
    '&:hover': { boxShadow: '0 6px 16px 0 rgba(0, 0, 0, 0.22)' },
    '&:active': { transform: 'scale(0.94)' },
  },
}

export function getLabel(status, hasCall) {
  if (status === VoiceCallStatus.Initiated && hasCall) return 'Connection...'
  if (status === VoiceCallStatus.Ringing && hasCall) return 'Ringing...'
  if (status === VoiceCallStatus.InProgress && hasCall) return 'In Progress'
  if (status === VoiceCallStatus.Completed) return 'Finished'
  if (status === VoiceCallStatus.Failed) return 'Failed'
  if (status === VoiceCallStatus.Busy) return 'Busy'
  if (status === VoiceCallStatus.NoAnswer) return 'No Answer'
  return 'Phone:'
}

export function Handle(props) {
  return (
    <Box {...props} sx={styles.handle}>
      <Box sx={styles.bar} />
    </Box>
  )
}

export function MicPermission({ children }) {
  const { hasAudioInputDevices } = useDevices()
  const { error, isError } = useMicPermission()

  if (!isError)
    return (
      <Fade in>
        <div>{children}</div>
      </Fade>
    )

  const { title, content } = getMediaErrorDescription(error, hasAudioInputDevices)

  return (
    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
      {title}
      <br />
      {content}
    </Typography>
  )
}
